import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import axiosInstance from '../../utils/setupAxios';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardRow,
  MobileCardFooter,
} from '../common/ListMobileLayout';

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 500;

const getInfoValue = (information, type) => {
  if (!Array.isArray(information)) return null;
  const found = information.find((i) => i.type === type);
  return found ? found.value : null;
};

const formatDate = (ts) => {
  if (!ts) return '-';
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return isNaN(d.getTime()) ? String(ts) : d.toLocaleDateString();
};

const formatPrice = (v) => {
  if (v == null || v === '' || parseFloat(v) === 0) return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const renderPriceCell = (v) => {
  if (v == null || v === '') return '-';
  if (typeof v !== 'object') {
    const s = String(v).trim();
    return s === '' ? '-' : s;
  }
  // Nested object — try common price-value fields first
  for (const key of ['price', 'value', 'amount', 'retail', 'avg']) {
    if (v[key] != null && v[key] !== '') return formatPrice(v[key]) ?? String(v[key]);
  }
  // Flatten all non-null entries
  const entries = Object.entries(v).filter(([, val]) => val != null && val !== '');
  if (entries.length === 0) return '-';
  return entries.map(([k, val]) => `${k}: ${typeof val === 'object' ? JSON.stringify(val) : val}`).join(' / ');
};

const SOURCE_BADGE = {
  coininapp: { bg: 'bg-purple-lt', text: 'text-purple', label: 'CoinInApp' },
  coinsnap:  { bg: 'bg-cyan-lt',   text: 'text-cyan',   label: 'CoinSnap'  },
};

const isValidImgUrl = (url) => typeof url === 'string' && url.trim() !== '' && !/\.html?$/i.test(url);

const getCoinThumb = (coin) => {
  if (coin.grade_info_data) {
    if (isValidImgUrl(coin.grade_info_data.ref_img_front)) return coin.grade_info_data.ref_img_front;
    if (isValidImgUrl(coin.grade_info_data.ref_img_back)) return coin.grade_info_data.ref_img_back;
  }
  const imgs = Array.isArray(coin.images) ? coin.images : [];
  return imgs.find(isValidImgUrl) ?? null;
};

const toYMD = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function CoinsList() {
  const [searchInput, setSearchInput] = useState('');
  const [countryInput, setCountryInput] = useState('');
  const [sourceInput, setSourceInput] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [query, setQuery] = useState('');
  const [country, setCountry] = useState('');
  const [source, setSource] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState(null);
  const [appliedDateTo, setAppliedDateTo] = useState(null);
  const [page, setPage] = useState(1);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  const fetchCoins = useCallback(async (q, c, s, df, dt, p) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: p, items_per_page: PAGE_SIZE };
      if (q) params.q = q;
      if (c) params.country = c;
      if (s) params.source = s;
      if (df) params.from_date = toYMD(df);
      if (dt) params.to_date = toYMD(dt);
      const res = await axiosInstance.get('/coins/get', { params });
      const payload = res?.data?.data ?? res?.data;
      if (payload?.items && Array.isArray(payload.items)) {
        setItems(payload.items);
        const t = payload.objects?.total ?? payload.items.length;
        setTotal(t);
        setLastPage(Math.max(1, Math.ceil(t / PAGE_SIZE)));
      } else if (Array.isArray(payload)) {
        setItems(payload);
        setTotal(payload.length);
        setLastPage(1);
      } else {
        setItems([]);
        setTotal(0);
        setLastPage(1);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load coins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axiosInstance.get('/coins/get-countries')
      .then((res) => {
        const data = res?.data?.data ?? res?.data;
        if (Array.isArray(data)) setCountries(data);
      })
      .catch(() => {});
  }, []);

  // Debounced sync from inputs → committed states
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setQuery(searchInput.trim());
      setCountry(countryInput.trim());
      setSource(sourceInput);
      setAppliedDateFrom(dateFrom);
      setAppliedDateTo(dateTo);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput, countryInput, sourceInput, dateFrom, dateTo]);

  useEffect(() => {
    fetchCoins(query, country, source, appliedDateFrom, appliedDateTo, page);
  }, [query, country, source, appliedDateFrom, appliedDateTo, page, fetchCoins]);

  const handleClear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput('');
    setCountryInput('');
    setSourceInput('');
    setDateFrom(null);
    setDateTo(null);
    setQuery('');
    setCountry('');
    setSource('');
    setAppliedDateFrom(null);
    setAppliedDateTo(null);
    setPage(1);
  };

  const hasFilters = searchInput || countryInput || sourceInput || dateFrom || dateTo;

  const pageNumbers = () => {
    const cur = page;
    const start = Math.max(1, Math.min(cur - 2, lastPage - 4));
    const end = Math.min(lastPage, start + 4);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const PaginationBar = () => (
    <div className="d-flex align-items-center px-3 py-2">
      <p className="m-0 text-muted small">
        Page <strong>{page}</strong> / <strong>{lastPage}</strong>
        {' · '}{(total ?? 0).toLocaleString()} total
      </p>
      {lastPage > 1 && (
        <ul className="pagination pagination-sm m-0 ms-auto">
          <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
          </li>
          {pageNumbers().map((n) => (
            <li key={n} className={`page-item ${n === page ? 'active' : ''}`}>
              <button className="page-link" onClick={() => setPage(n)}>{n}</button>
            </li>
          ))}
          <li className={`page-item ${page >= lastPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>›</button>
          </li>
        </ul>
      )}
    </div>
  );

  const renderCoinMobileCard = (coin) => {
    const thumb = getCoinThumb(coin);
    const countryVal = getInfoValue(coin.information, 'country') || '—';
    const coinType = getInfoValue(coin.information, 'coin_type');
    const minP = formatPrice(coin.min_price);
    const maxP = formatPrice(coin.max_price);
    const sourceBadge = SOURCE_BADGE[coin.source];

    return (
      <MobileCard key={coin.id}>
        <MobileCardTop>
          {thumb ? (
            <img
              src={thumb}
              alt={coin.name}
              style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6', flexShrink: 0 }}
            />
          ) : null}
          <MobileCardTitle>{coin.name || '—'}</MobileCardTitle>
        </MobileCardTop>
        <MobileCardRow label="ID" mono>{coin.id}</MobileCardRow>
        {coin.tid ? <MobileCardRow label="TID" mono>{coin.tid}</MobileCardRow> : null}
        <MobileCardRow label="Country">{countryVal}</MobileCardRow>
        <MobileCardRow label="Source">
          {sourceBadge ? (
            <span className={`badge ${sourceBadge.bg} ${sourceBadge.text}`}>{sourceBadge.label}</span>
          ) : (coin.source || '—')}
        </MobileCardRow>
        <MobileCardRow label="Type">{coinType || '—'}</MobileCardRow>
        <MobileCardRow label="Price">
          {minP || maxP ? (minP && maxP ? `${minP} – ${maxP}` : minP || maxP) : 'N/A'}
        </MobileCardRow>
        <MobileCardRow label="Added">{formatDate(coin.created_at)}</MobileCardRow>
        <MobileCardFooter>
          <button className="btn btn-sm btn-outline-primary" onClick={() => setSelected(coin)}>
            View
          </button>
        </MobileCardFooter>
      </MobileCard>
    );
  };

  return (
    <div className="dashboard-list-page">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title">Coins</h2>
            {!loading && (
              <div className="text-muted mt-1">
                {(total ?? 0).toLocaleString()} coins in database
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-sm-5">
              <label className="form-label mb-1">Name / Keyword</label>
              <input
                className="form-control"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Eagle, Liberty, Morgan…"
              />
            </div>
            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Country</label>
              <select
                className="form-select"
                value={countryInput}
                onChange={(e) => setCountryInput(e.target.value)}
              >
                <option value="">All countries</option>
                {countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Source</label>
              <select
                className="form-select"
                value={sourceInput}
                onChange={(e) => setSourceInput(e.target.value)}
              >
                <option value="">All</option>
                <option value="coininapp">CoinInApp</option>
                <option value="coinsnap">CoinSnap</option>
              </select>
            </div>
            <div className="col-12 col-sm-auto">
              <label className="form-label mb-1">From</label>
              <Flatpickr
                className="form-control"
                value={dateFrom || ''}
                options={{ dateFormat: 'Y-m-d', maxDate: dateTo || undefined }}
                onChange={([d]) => setDateFrom(d || null)}
                placeholder="Start date"
              />
            </div>
            <div className="col-12 col-sm-auto">
              <label className="form-label mb-1">To</label>
              <Flatpickr
                className="form-control"
                value={dateTo || ''}
                options={{ dateFormat: 'Y-m-d', minDate: dateFrom || undefined }}
                onChange={([d]) => setDateTo(d || null)}
                placeholder="End date"
              />
            </div>
            {hasFilters && (
              <div className="col-auto">
                <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>
                  Clear filter
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {error && <div className="alert alert-danger m-3 mb-0">{error}</div>}

        {/* Pagination — top */}
        {!loading && total > 0 && <PaginationBar />}

        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-muted py-5">No coins found.</div>
        ) : (
          <>
            <ListDesktop>
              <table className="table table-vcenter table-hover card-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}>ID</th>
                    <th style={{ width: 60 }}>Image</th>
                    <th>Name</th>
                    <th style={{ width: 150 }}>Country</th>
                    <th style={{ width: 110 }}>Source</th>
                    <th style={{ width: 120 }}>Type</th>
                    <th style={{ width: 160 }}>Price Range</th>
                    <th style={{ width: 110 }}>Added</th>
                    <th style={{ width: 70 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((coin) => {
                    const thumb = getCoinThumb(coin);
                    const countryVal = getInfoValue(coin.information, 'country') || '-';
                    const coinType = getInfoValue(coin.information, 'coin_type');
                    const minP = formatPrice(coin.min_price);
                    const maxP = formatPrice(coin.max_price);

                    return (
                      <tr key={coin.id}>
                        <td className="text-muted small">{coin.id}</td>
                        <td>
                          {thumb ? (
                            <img
                              src={thumb}
                              alt={coin.name}
                              style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6' }}
                            />
                          ) : (
                            <div style={{ width: 42, height: 42, background: '#f1f3f4', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd', fontSize: 20 }}>
                              ○
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="fw-semibold">{coin.name || '-'}</div>
                          {coin.tid && <div className="text-muted small">TID: {coin.tid}</div>}
                        </td>
                        <td>
                          {countryVal !== '-' ? (
                            <span className="badge bg-blue-lt text-blue" style={{ fontWeight: 500 }}>
                              {countryVal}
                            </span>
                          ) : <span className="text-muted">-</span>}
                        </td>
                        <td>
                          {(() => {
                            const s = SOURCE_BADGE[coin.source];
                            return s
                              ? <span className={`badge ${s.bg} ${s.text}`} style={{ fontSize: '0.7rem' }}>{s.label}</span>
                              : <span className="text-muted small">{coin.source || '-'}</span>;
                          })()}
                        </td>
                        <td>
                          {coinType ? (
                            <span className="badge bg-secondary-lt text-secondary" style={{ fontSize: '0.7rem' }}>
                              {coinType}
                            </span>
                          ) : <span className="text-muted">-</span>}
                        </td>
                        <td>
                          {minP || maxP ? (
                            <span className="text-green fw-semibold small">
                              {minP && maxP ? `${minP} – ${maxP}` : minP || maxP}
                            </span>
                          ) : (
                            <span className="text-muted small">N/A</span>
                          )}
                        </td>
                        <td className="text-muted small">{formatDate(coin.created_at)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setSelected(coin)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ListDesktop>

            <ListMobile>
              {items.map((coin) => renderCoinMobileCard(coin))}
            </ListMobile>
          </>
        )}

        {/* Pagination — bottom */}
        {!loading && total > 0 && (
          <div className="card-footer p-0">
            <PaginationBar />
          </div>
        )}
      </div>

      {selected && (
        <CoinDetailModal coin={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function CoinDetailModal({ coin, onClose }) {
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => { document.body.classList.remove('modal-open'); };
  }, []);

  const information = Array.isArray(coin.information) ? coin.information : [];
  const prices = Array.isArray(coin.prices) ? coin.prices : [];
  const priceKeys = prices.length > 0 ? Object.keys(prices[0]) : [];

  // Build image list: prefer grade_info_data front/back, fall back to images[]
  // Skip any URL ending in .htm/.html — those are bad coinsnap references
  const displayImages = (() => {
    const gd = coin.grade_info_data;
    if (gd && (isValidImgUrl(gd.ref_img_front) || isValidImgUrl(gd.ref_img_back))) {
      const imgs = [];
      if (isValidImgUrl(gd.ref_img_front)) imgs.push({ url: gd.ref_img_front, label: 'Front' });
      if (isValidImgUrl(gd.ref_img_back)) imgs.push({ url: gd.ref_img_back, label: 'Back' });
      return imgs;
    }
    const fallback = Array.isArray(coin.images) ? coin.images : [];
    return fallback.filter(isValidImgUrl).map((url, i) => ({
      url,
      label: i === 0 ? 'Obverse' : i === 1 ? 'Reverse' : `Image ${i + 1}`,
    }));
  })();

  const countryVal = getInfoValue(information, 'country');
  const coinType = getInfoValue(information, 'coin_type');
  const denomination = getInfoValue(information, 'denomination');
  const composition = getInfoValue(information, 'composition');
  const yearFrom = getInfoValue(information, 'year_range_from');
  const yearTo = getInfoValue(information, 'year_range_to');

  const minP = formatPrice(coin.min_price);
  const maxP = formatPrice(coin.max_price);

  const topInfoTypes = new Set(['country', 'coin_type', 'denomination', 'composition', 'year_range_from', 'year_range_to']);
  const extraInfo = information.filter((i) => !topInfoTypes.has(i.type));

  return ReactDOM.createPortal(
    <>
      <div className="modal-backdrop fade show" />
      <div
        className="modal fade show"
        style={{ display: 'block' }}
        tabIndex="-1"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable modal-fullscreen-md-down">
          <div className="modal-content">

            <div className="modal-header">
              <div>
                <h5 className="modal-title mb-1">{coin.name || 'Coin Detail'}</h5>
                <div className="d-flex flex-wrap gap-1">
                  {countryVal && <span className="badge bg-blue-lt text-blue">{countryVal}</span>}
                  {coinType && <span className="badge bg-secondary-lt text-secondary">{coinType}</span>}
                  {denomination && <span className="badge bg-green-lt text-green">{denomination}</span>}
                  {composition && <span className="badge bg-orange-lt text-orange">{composition}</span>}
                </div>
              </div>
              <button type="button" className="btn-close ms-3" onClick={onClose} />
            </div>

            <div className="modal-body">

              {/* Images */}
              {displayImages.length > 0 && (
                <div className="mb-4">
                  <div className="text-muted small fw-semibold text-uppercase mb-2">Images</div>
                  <div className="d-flex gap-3 flex-wrap">
                    {displayImages.map(({ url, label }, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer" className="d-block" style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #dee2e6' }}>
                        <img
                          src={url}
                          alt={label}
                          style={{ width: 130, height: 130, objectFit: 'contain', background: '#f8f9fa', display: 'block' }}
                        />
                        <div className="text-center text-muted small py-1" style={{ background: '#f8f9fa' }}>
                          {label}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Overview */}
              <div className="mb-4">
                <div className="text-muted small fw-semibold text-uppercase mb-2">Overview</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}>
                  {[
                    { label: 'ID', value: coin.id },
                    { label: 'TID', value: coin.tid || '-' },
                    ...(yearFrom ? [{ label: 'Year', value: yearFrom === yearTo || !yearTo ? yearFrom : `${yearFrom} – ${yearTo}` }] : []),
                    { label: 'Min Price', value: minP || 'N/A' },
                    { label: 'Max Price', value: maxP || 'N/A' },
                    { label: 'Added', value: formatDate(coin.created_at) },
                    { label: 'Updated', value: formatDate(coin.updated_at) },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ minWidth: 120 }}>
                      <div className="text-muted small">{label}</div>
                      <div className="fw-semibold">{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specifications */}
              {extraInfo.length > 0 && (
                <div className="mb-4">
                  <div className="text-muted small fw-semibold text-uppercase mb-2">Specifications</div>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0">
                      <tbody>
                        {extraInfo.map((row, i) => (
                          <tr key={i}>
                            <td className="text-muted" style={{ width: '40%', background: '#f8f9fa' }}>{row.name || row.type}</td>
                            <td>{String(row.value ?? '-')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Price grades */}
              {prices.length > 0 && priceKeys.length > 0 && (
                <div>
                  <div className="text-muted small fw-semibold text-uppercase mb-2">Price Grades</div>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0">
                      <thead className="table-light">
                        <tr>{priceKeys.map((k) => <th key={k}>{k}</th>)}</tr>
                      </thead>
                      <tbody>
                        {prices.map((row, i) => (
                          <tr key={i}>
                            {priceKeys.map((k) => <td key={k}>{renderPriceCell(row[k])}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
